export type Boundaries = [left: string, right: string];

export type Fragment = { text: string } | { variable: string };

/**
 * Parses the template string into an array of fragments containing text and variables.
 * Uses simple string matching for boundaries and handles malformed templates gracefully.
 * Takes O(n) time and O(1) additional space, where n is the length of the input string.
 *
 * @param template The template string to parse.
 * @param [left, right] The template boundaries as a tuple.
 *
 * @returns An array of fragments representing the parsed template.
 */
export function parse(template: string, [left, right]: Boundaries): Array<Fragment> {
	const leftLength = left.length;
	const rightLength = right.length;

	/**
	 * Keep track of the position of the last found left boundary.
	 * The nested variables are not supported, so we only need to track one level.
	 */
	let leftBoundaryIndex: undefined | number;
	let textStart = 0;
	const fragments: Array<Fragment> = [];

	for (let index = 0; index < template.length; index++) {
		// Check for left boundary
		if (template.startsWith(left, index)) {
			leftBoundaryIndex = index;
			index += leftLength - 1; // Move index to end of left boundary
		}

		// Check for right boundary
		else if (leftBoundaryIndex != null && template.startsWith(right, index)) {
			// Capture text before the left boundary
			const textEnd = leftBoundaryIndex;

			// Ignore empty text segments
			if (textStart < textEnd) {
				const text = template.substring(textStart, textEnd);

				fragments.push({ text });
			}

			const variableStart = leftBoundaryIndex + leftLength; // Start of variable name
			const variableEnd = index; // End of variable name
			const variable = template.substring(variableStart, variableEnd).trim();

			// Ignore empty variable names
			if (variable.length > 0) {
				fragments.push({ variable });
			}

			index += rightLength - 1; // Move index to end of right boundary
			textStart = index + 1;
			leftBoundaryIndex = undefined; // Reset left boundary index
		}
	}

	if (textStart < template.length) {
		const text = template.substring(textStart, template.length);

		fragments.push({ text });
	}

	return fragments;
}
